/** Pure state-machine + handle-math for the construction-line
 *  bezier cycle. No paper.js dependency — handles are passed as
 *  plain `{x, y}` objects so this module is unit-testable without
 *  a canvas. The thin wrapper in `ConstructionTool.ts`
 *  (`cycleAnchorBezier`) parses pathData via paper, computes the
 *  bootstrap tangent + magnitude from the segment's neighbors,
 *  then defers all decision-making to `computeBezierCycleStep`. */

export type Point2 = { x: number; y: number };
export type LockMode = "angle" | "smooth" | null;
export type AnchorState = 0 | 1 | 2 | 3;

/** Per-anchor memory carried across cycle edits. `handleIn` /
 *  `handleOut` are the live snapshot from the most recent
 *  `X → 0` transition; `preLock` and `preMirror` are the user's
 *  pre-lock and angle-only-state captures (kept while the chain
 *  remains "pure" — no manual handle drag has happened). */
export type AnchorMemory = {
  handleIn: Point2;
  handleOut: Point2;
  preLock?: { handleIn: Point2; handleOut: Point2 };
  preMirror?: { handleIn: Point2; handleOut: Point2 };
};

export type CycleStepInput = {
  /** Current handles on the segment, as offsets from the anchor. */
  handleIn: Point2;
  handleOut: Point2;
  /** Lock mode currently set on the anchor (from `smoothAnchors`). */
  currentMode: LockMode;
  /** True when the anchor is the first or last segment of an open
   *  subpath — collapses the cycle to a 0 ↔ 1 toggle. */
  isEndpoint: boolean;
  /** True only when `isEndpoint` and the segment is the FIRST one
   *  of the subpath (segmentIndex === 0). Affects which side
   *  receives the bootstrap tangent on a 0 → 1 transition. */
  isFirstEndpoint: boolean;
  /** Default tangent direction (already normalized) for the
   *  no-curve → curved bootstrap. Caller computes it from the
   *  segment's neighbors. Ignored when the cycle restores from
   *  memory instead of bootstrapping. */
  bootstrapTangent: Point2;
  /** Default tangent magnitude for the bootstrap. */
  bootstrapMagnitude: number;
  /** Saved memory entry from a previous cycle round, if any. */
  memory: AnchorMemory | undefined;
};

export type CycleStepResult = {
  /** New handles after the transition, as offsets from the anchor. */
  handleIn: Point2;
  handleOut: Point2;
  /** New lock mode (drives `smoothAnchors` entry presence + mode). */
  mode: LockMode;
  /** Updated memory entry, or `undefined` to clear it. */
  memory: AnchorMemory | undefined;
  /** History label for the commit. */
  label: string;
};

const TOLERANCE = 0.001;

const isZero = (h: Point2): boolean =>
  Math.abs(h.x) < TOLERANCE && Math.abs(h.y) < TOLERANCE;

const samePoint = (a: Point2, b: Point2): boolean =>
  Math.abs(a.x - b.x) < TOLERANCE && Math.abs(a.y - b.y) < TOLERANCE;

const cloneHandles = (h: { handleIn: Point2; handleOut: Point2 }) => ({
  handleIn: { x: h.handleIn.x, y: h.handleIn.y },
  handleOut: { x: h.handleOut.x, y: h.handleOut.y },
});

/** Map current handles + mode to the 4-state cycle position. */
export function determineState(
  handleIn: Point2,
  handleOut: Point2,
  currentMode: LockMode,
): AnchorState {
  if (currentMode === "smooth") return 3;
  if (currentMode === "angle") return 2;
  return isZero(handleIn) && isZero(handleOut) ? 0 : 1;
}

/** Pick the next state in the cycle. Endpoints collapse to 0 ↔ 1. */
export function nextStateInCycle(
  current: AnchorState,
  isEndpoint: boolean,
): AnchorState {
  if (isEndpoint) return current === 0 ? 1 : 0;
  return ((current + 1) % 4) as AnchorState;
}

/** True when the segment's handles still match the geometry the
 *  cycle would produce by purely applying the lock formulas to
 *  `preLock`. Used at `X → 0` to decide whether to keep the
 *  pre-lock snapshot — once the user has dragged a handle in
 *  state 2 / 3 the snapshot is no longer faithful. */
export function handlesMatchPureLock(
  handleIn: Point2,
  handleOut: Point2,
  state: 2 | 3,
  preLock: { handleIn: Point2; handleOut: Point2 },
): boolean {
  const close = (a: number, b: number) => Math.abs(a - b) < TOLERANCE;
  const preIn = preLock.handleIn;
  const preOut = preLock.handleOut;
  if (state === 3) {
    return (
      close(handleOut.x, preOut.x) &&
      close(handleOut.y, preOut.y) &&
      close(handleIn.x, -preOut.x) &&
      close(handleIn.y, -preOut.y)
    );
  }
  // state === 2 — angle-only formula. Primary side picks handleOut
  // when it has length, else handleIn.
  const outLen = Math.hypot(preOut.x, preOut.y);
  if (outLen > 0) {
    if (!close(handleOut.x, preOut.x) || !close(handleOut.y, preOut.y))
      return false;
    const inLen = Math.hypot(preIn.x, preIn.y);
    const targetLen = inLen > 0 ? inLen : outLen;
    return (
      close(handleIn.x, (-preOut.x / outLen) * targetLen) &&
      close(handleIn.y, (-preOut.y / outLen) * targetLen)
    );
  }
  const inLen = Math.hypot(preIn.x, preIn.y);
  if (inLen === 0) return false;
  if (!close(handleIn.x, preIn.x) || !close(handleIn.y, preIn.y)) return false;
  return close(handleOut.x, -preIn.x) && close(handleOut.y, -preIn.y);
}

/** True when the segment's handles still match the 2 → 3 mirror
 *  applied to `preMirror`. Used at `3 → 0` to decide whether to
 *  keep the angle-only snapshot. */
export function handlesMatchMirror(
  handleIn: Point2,
  handleOut: Point2,
  preMirror: { handleIn: Point2; handleOut: Point2 },
): boolean {
  const close = (a: number, b: number) => Math.abs(a - b) < TOLERANCE;
  return (
    close(handleOut.x, preMirror.handleOut.x) &&
    close(handleOut.y, preMirror.handleOut.y) &&
    close(handleIn.x, -preMirror.handleOut.x) &&
    close(handleIn.y, -preMirror.handleOut.y)
  );
}

/** Apply one step of the bezier cycle to an anchor. See module
 *  doc comment for the high-level model. */
export function computeBezierCycleStep(input: CycleStepInput): CycleStepResult {
  const {
    handleIn,
    handleOut,
    currentMode,
    isEndpoint,
    isFirstEndpoint,
    bootstrapTangent,
    bootstrapMagnitude,
    memory,
  } = input;

  const currentState = determineState(handleIn, handleOut, currentMode);
  const nextState = nextStateInCycle(currentState, isEndpoint);

  let newHandleIn: Point2 = { x: handleIn.x, y: handleIn.y };
  let newHandleOut: Point2 = { x: handleOut.x, y: handleOut.y };
  let nextMemory: AnchorMemory | undefined = memory;

  if (nextState === 0) {
    // Snapshot live handles before zeroing — preserve preLock /
    // preMirror only if the live geometry is still consistent with
    // their pure derivation.
    if (!isZero(handleIn) || !isZero(handleOut)) {
      const keepPreLock =
        memory?.preLock &&
        (currentState === 2 || currentState === 3) &&
        handlesMatchPureLock(handleIn, handleOut, currentState, memory.preLock)
          ? cloneHandles(memory.preLock)
          : undefined;
      const keepPreMirror =
        memory?.preMirror &&
        currentState === 3 &&
        handlesMatchMirror(handleIn, handleOut, memory.preMirror)
          ? cloneHandles(memory.preMirror)
          : undefined;
      nextMemory = {
        handleIn: { x: handleIn.x, y: handleIn.y },
        handleOut: { x: handleOut.x, y: handleOut.y },
        ...(keepPreLock ? { preLock: keepPreLock } : {}),
        ...(keepPreMirror ? { preMirror: keepPreMirror } : {}),
      };
    }
    newHandleIn = { x: 0, y: 0 };
    newHandleOut = { x: 0, y: 0 };
  } else if (currentState === 0 && nextState === 1) {
    // Restore preference: preLock > preMirror > latest snap >
    // bootstrap. Memory entry is kept intact so a later 1 → 2 can
    // still restore from preMirror.
    if (memory?.preLock) {
      newHandleIn = {
        x: memory.preLock.handleIn.x,
        y: memory.preLock.handleIn.y,
      };
      newHandleOut = {
        x: memory.preLock.handleOut.x,
        y: memory.preLock.handleOut.y,
      };
    } else if (memory?.preMirror) {
      newHandleIn = {
        x: memory.preMirror.handleIn.x,
        y: memory.preMirror.handleIn.y,
      };
      newHandleOut = {
        x: memory.preMirror.handleOut.x,
        y: memory.preMirror.handleOut.y,
      };
    } else if (memory) {
      newHandleIn = { x: memory.handleIn.x, y: memory.handleIn.y };
      newHandleOut = { x: memory.handleOut.x, y: memory.handleOut.y };
    } else {
      const t = bootstrapTangent;
      const m = bootstrapMagnitude;
      if (isEndpoint) {
        if (isFirstEndpoint) {
          newHandleOut = { x: t.x * m, y: t.y * m };
          newHandleIn = { x: 0, y: 0 };
        } else {
          newHandleIn = { x: -t.x * m, y: -t.y * m };
          newHandleOut = { x: 0, y: 0 };
        }
      } else {
        newHandleOut = { x: t.x * m, y: t.y * m };
        newHandleIn = { x: -t.x * m, y: -t.y * m };
      }
    }
  } else if (currentState === 1 && nextState === 2) {
    // 1 → 2: capture preLock; if chain is still pure and a
    // preMirror exists from a previous round, restore it instead
    // of running the angle-only formula.
    const newPreLock = {
      handleIn: { x: handleIn.x, y: handleIn.y },
      handleOut: { x: handleOut.x, y: handleOut.y },
    };
    const chainPure =
      memory !== undefined &&
      ((memory.preLock !== undefined &&
        samePoint(newPreLock.handleIn, memory.preLock.handleIn) &&
        samePoint(newPreLock.handleOut, memory.preLock.handleOut)) ||
        (memory.preMirror !== undefined &&
          samePoint(newPreLock.handleIn, memory.preMirror.handleIn) &&
          samePoint(newPreLock.handleOut, memory.preMirror.handleOut)) ||
        (samePoint(newPreLock.handleIn, memory.handleIn) &&
          samePoint(newPreLock.handleOut, memory.handleOut)));

    if (chainPure && memory?.preMirror) {
      newHandleIn = {
        x: memory.preMirror.handleIn.x,
        y: memory.preMirror.handleIn.y,
      };
      newHandleOut = {
        x: memory.preMirror.handleOut.x,
        y: memory.preMirror.handleOut.y,
      };
      nextMemory = {
        handleIn: { x: memory.handleIn.x, y: memory.handleIn.y },
        handleOut: { x: memory.handleOut.x, y: memory.handleOut.y },
        ...(memory.preLock ? { preLock: cloneHandles(memory.preLock) } : {}),
        preMirror: cloneHandles(memory.preMirror),
      };
    } else {
      // Apply the angle-lock formula.
      const primaryIsOut = !isZero(handleOut);
      const primary = primaryIsOut ? handleOut : handleIn;
      const secondary = primaryIsOut ? handleIn : handleOut;
      const primaryLen = Math.hypot(primary.x, primary.y);
      let secondaryNew: Point2 = secondary;
      if (primaryLen > 0) {
        const secondaryLen = Math.hypot(secondary.x, secondary.y);
        const targetLen = secondaryLen > 0 ? secondaryLen : primaryLen;
        const dirX = -primary.x / primaryLen;
        const dirY = -primary.y / primaryLen;
        secondaryNew = { x: dirX * targetLen, y: dirY * targetLen };
      }
      if (primaryIsOut) {
        newHandleIn = secondaryNew;
        newHandleOut = { x: handleOut.x, y: handleOut.y };
      } else {
        newHandleOut = secondaryNew;
        newHandleIn = { x: handleIn.x, y: handleIn.y };
      }
      nextMemory = {
        handleIn: memory
          ? { x: memory.handleIn.x, y: memory.handleIn.y }
          : { x: newPreLock.handleIn.x, y: newPreLock.handleIn.y },
        handleOut: memory
          ? { x: memory.handleOut.x, y: memory.handleOut.y }
          : { x: newPreLock.handleOut.x, y: newPreLock.handleOut.y },
        preLock: newPreLock,
        // preMirror cleared — the chain was broken by drag-in-state-1
        // or this is the first time we've reached state 2.
      };
    }
  } else if (currentState === 2 && nextState === 3) {
    // 2 → 3: capture state-2 handles as preMirror BEFORE applying
    // the mirror formula; preLock is unchanged.
    const preMirror = {
      handleIn: { x: handleIn.x, y: handleIn.y },
      handleOut: { x: handleOut.x, y: handleOut.y },
    };
    nextMemory = {
      handleIn: memory
        ? { x: memory.handleIn.x, y: memory.handleIn.y }
        : { x: preMirror.handleIn.x, y: preMirror.handleIn.y },
      handleOut: memory
        ? { x: memory.handleOut.x, y: memory.handleOut.y }
        : { x: preMirror.handleOut.x, y: preMirror.handleOut.y },
      ...(memory?.preLock ? { preLock: cloneHandles(memory.preLock) } : {}),
      preMirror,
    };
    if (!isZero(handleOut)) {
      newHandleIn = { x: -handleOut.x, y: -handleOut.y };
      newHandleOut = { x: handleOut.x, y: handleOut.y };
    } else if (!isZero(handleIn)) {
      newHandleOut = { x: -handleIn.x, y: -handleIn.y };
      newHandleIn = { x: handleIn.x, y: handleIn.y };
    }
  }

  const newMode: LockMode =
    nextState === 2 ? "angle" : nextState === 3 ? "smooth" : null;

  const label =
    nextState === 0
      ? "Remove anchor curves"
      : nextState === 1
        ? "Curve anchor"
        : nextState === 2
          ? "Lock anchor angle"
          : "Lock anchor smooth";

  return {
    handleIn: newHandleIn,
    handleOut: newHandleOut,
    mode: newMode,
    memory: nextMemory,
    label,
  };
}
